import type { AgentMessage, Message } from "@/hooks/use-stream-conversation";
import { useMemo } from "react";

export type InjectionAnchor = {
    key: string;
    afterMessageId: string | number | null;
};

export type InjectionSpec = {
    anchorKey: string;
    messages: AgentMessage[];
};

/**
 * Splices injected messages into the conversation stream at their anchored
 * positions. Each spec is matched to an anchor by key, and its messages are
 * inserted sequentially after the anchor point.
 */
export function useInjectedMessages(
    messages: Message[],
    anchors: InjectionAnchor[],
    injectedSpecs: InjectionSpec[],
): Message[] {
    return useMemo(() => {
        if (anchors.length === 0 || injectedSpecs.length === 0) {
            return messages;
        }

        const result = [...messages];

        // Process anchors in reverse insertion-index order so splice indices stay valid
        const anchorEntries = anchors
            .map((anchor) => {
                const spec = injectedSpecs.find((s) => s.anchorKey === anchor.key);
                if (!spec || spec.messages.length === 0) return null;

                const idx =
                    anchor.afterMessageId !== null ? result.findIndex((m) => m.id === anchor.afterMessageId) : -1;

                return { anchor, spec, idx };
            })
            .filter((e): e is NonNullable<typeof e> => e !== null)
            .sort((a, b) => b.idx - a.idx);

        for (const { spec, idx } of anchorEntries) {
            if (idx !== -1) {
                result.splice(idx + 1, 0, ...spec.messages);
            }
        }

        return result;
    }, [messages, anchors, injectedSpecs]);
}
